'use client'

export const dynamic = 'force-dynamic'

import { useCallback, useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { ArrowLeft, Check, Loader2, MapPin, Plus, Search, Users, X } from 'lucide-react'
import ImageUpload from '../../../components/ImageUpload'
import { PROFESSION_OPTIONS, CONTRIBUTION_OPTIONS, SCOPE_OPTIONS, ANCHOR_DEPENDENT_SCOPES } from '../../../lib/connect-options'

/* Masjid Connect — "How I can serve" own-profile editor. Talks only to /api/connect/profile
   (GET on mount, PUT on save). The whole /connect surface is gated by the masjidConnect flag
   in middleware, so this page 404s while the flag is off; nothing here assumes it's public.

   category is NO LONGER a user field — it's auto-derived server-side from `profession`. */

// Profession options grouped for the <optgroup> dropdown (order preserved from the constant).
const PROFESSION_GROUPS = PROFESSION_OPTIONS.reduce<Array<{ group: string; items: typeof PROFESSION_OPTIONS }>>((acc, opt) => {
  const last = acc[acc.length - 1]
  if (last && last.group === opt.group) last.items.push(opt)
  else acc.push({ group: opt.group, items: [opt] })
  return acc
}, [])
const isOtherProfession = (v: string) => v === 'other' || v.startsWith('other_')

const VISIBILITIES: Array<{ value: string; label: string; help: string }> = [
  { value: 'on_request', label: 'On request', help: 'You appear in search; contact details are shared only after you approve a request. Recommended.' },
  { value: 'masjid_only', label: 'Masjid only', help: 'Only members connected to your vouching masjid can find you.' },
  { value: 'open', label: 'Open', help: 'Anyone on Masjids.in can find your profile. Contact is still request-gated.' },
]

const NAVY = '#0B132B', GREEN = '#0E7C61', SLATE = '#5b6472', LINE = '#e6e4dc'

type Masjid = { id: string; name: string; city: string }
// A masjid the user has chosen to serve, carrying its OWN per-masjid direct-contact consent
// (default false = opt-in). This is the authoritative value written to that join row.
type SelectedMasjid = { id: string; name: string; city: string; directContactOk: boolean }

export default function ConnectProfilePage() {
  const [state, setState] = useState<'loading' | 'signin' | 'ready' | 'error'>('loading')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null)

  const [profession, setProfession] = useState('')
  const [professionOther, setProfessionOther] = useState('')
  const [contributionTypes, setContributionTypes] = useState<string[]>([])
  const [contributionOther, setContributionOther] = useState('')
  const [scopeLevel, setScopeLevel] = useState('masjid')
  const [skillTags, setSkillTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const [blurb, setBlurb] = useState('')
  const [photoUrl, setPhotoUrl] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [visibility, setVisibility] = useState('on_request')
  // Masjids the user will serve — multi-select, each with its OWN direct-contact consent. The first
  // selected doubles as the profile's vouching/anchor masjid (talent_profiles.masjid_id) for scope +
  // backward-compat; the per-masjid consent is stored on the talent_profile_masjids join rows.
  const [selectedMasjids, setSelectedMasjids] = useState<SelectedMasjid[]>([])

  const load = useCallback(async () => {
    setState('loading')
    try {
      const res = await fetch('/api/connect/profile')
      if (res.status === 401) return setState('signin')
      if (!res.ok) return setState('error')
      const data = await res.json()
      const p = data.profile
      if (p) {
        setProfession(p.profession || '')
        setProfessionOther(p.professionOther || '')
        setContributionTypes(Array.isArray(p.contributionTypes) ? p.contributionTypes : [])
        setContributionOther(p.contributionOther || '')
        setScopeLevel(p.scopeLevel || 'masjid')
        setSkillTags(Array.isArray(p.skillTags) ? p.skillTags : [])
        setBlurb(p.blurb || '')
        setPhotoUrl(p.photoUrl || '')
        setWhatsapp(p.whatsapp || '')
        setVisibility(p.visibility || 'on_request')
        setSelectedMasjids(
          Array.isArray(p.masjids)
            ? p.masjids.map((m: any) => ({
                id: String(m.masjidId),
                name: String(m.name || 'Masjid'),
                city: String(m.city || ''),
                directContactOk: m.directContactOk === true,
              }))
            : [],
        )
      }
      setState('ready')
    } catch { setState('error') }
  }, [])
  useEffect(() => { void load() }, [load])

  function addTag() {
    const t = tagInput.trim().slice(0, 40)
    if (!t) return
    if (!skillTags.includes(t) && skillTags.length < 10) setSkillTags((s) => [...s, t])
    setTagInput('')
  }

  async function save() {
    setSaving(true); setMsg(null)
    try {
      const res = await fetch('/api/connect/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profession: profession || null,
          professionOther: profession && isOtherProfession(profession) ? professionOther : null,
          contributionTypes,
          contributionOther: contributionTypes.includes('other') ? contributionOther : null,
          scopeLevel,
          skillTags, blurb, photoUrl, whatsapp, visibility, isActive: true,
          // Anchor/backward-compat: the first selected masjid is the vouching masjid.
          masjidId: selectedMasjids[0]?.id ?? null,
          // Per-masjid serve links — each carries its OWN consent (never a flat copy).
          masjids: selectedMasjids.map((m) => ({ masjidId: m.id, directContactOk: m.directContactOk })),
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) { setMsg({ tone: 'err', text: data.error || 'Could not save. Please check the form.' }); return }
      setMsg({ tone: 'ok', text: 'Saved. Your profile is live to the community.' })
    } catch { setMsg({ tone: 'err', text: 'Could not save — please retry.' }) } finally { setSaving(false) }
  }

  // Everything is optional (the offer is skippable). Save is blocked only while a save is in flight.
  const canSave = !saving
  // Anchor-dependent scope with no masjid selected → honest inline nudge (never a hard block).
  const needsAnchor = ANCHOR_DEPENDENT_SCOPES.has(scopeLevel) && selectedMasjids.length === 0

  if (state === 'loading') return <Shell><div style={{ padding: 60, textAlign: 'center', color: SLATE }}><Loader2 className="spin" size={22} /> Loading…</div></Shell>
  if (state === 'signin') return <Shell><Notice title="Sign in to set up your Connect profile" body="Masjid Connect ties your profile to your account so you control who can reach you." cta={{ href: '/login?redirect=/connect/profile', label: 'Sign in' }} /></Shell>
  if (state === 'error') return <Shell><Notice title="Something went wrong" body="Your profile couldn’t be loaded right now." cta={{ href: '/connect/profile', label: 'Retry' }} /></Shell>

  return (
    <Shell>
      <div style={{ display: 'grid', gap: 18 }}>
        <Field label="Professional photo" hint="A clear, respectful photo. Separate from your account avatar.">
          <ImageUpload value={photoUrl} onChange={setPhotoUrl} label="Connect photo" folder="connect" context="avatar" />
        </Field>

        <Field label="Profession" hint="Optional. Helps your masjid community find the right help.">
          <select value={profession} onChange={(e) => setProfession(e.target.value)} style={input}>
            <option value="">Select a profession…</option>
            {PROFESSION_GROUPS.map((g) => (
              <optgroup key={g.group} label={g.group}>
                {g.items.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </optgroup>
            ))}
          </select>
          {profession && isOtherProfession(profession) && (
            <input value={professionOther} onChange={(e) => setProfessionOther(e.target.value.slice(0, 120))} placeholder="Tell us your profession" style={{ ...input, marginTop: 8 }} />
          )}
        </Field>

        <Field label="Skills" hint={`Add up to 10. ${skillTags.length}/10 used.`}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag() } }} placeholder="e.g. Paediatrics, Arabic tutoring" style={{ ...input, flex: 1 }} disabled={skillTags.length >= 10} />
            <button type="button" onClick={addTag} disabled={skillTags.length >= 10} style={chipBtn}><Plus size={15} /> Add</button>
          </div>
          {skillTags.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
              {skillTags.map((t) => (
                <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eef6f3', color: GREEN, border: '1px solid #cfe3dc', borderRadius: 999, padding: '5px 10px', fontSize: 13, fontWeight: 600 }}>
                  {t}<button type="button" aria-label={`Remove ${t}`} onClick={() => setSkillTags((s) => s.filter((x) => x !== t))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: GREEN, display: 'inline-flex' }}><X size={13} /></button>
                </span>
              ))}
            </div>
          )}
        </Field>

        <Field label="How would you like to contribute to your Masjid?" hint="Optional. Pick any that apply.">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {CONTRIBUTION_OPTIONS.map((c) => {
              const on = contributionTypes.includes(c.value)
              return (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setContributionTypes((s) => on ? s.filter((x) => x !== c.value) : [...s, c.value])}
                  style={{ border: `1.5px solid ${on ? GREEN : LINE}`, background: on ? '#eef6f3' : '#fff', color: on ? GREEN : NAVY, borderRadius: 999, padding: '7px 13px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  {on ? <Check size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} /> : null}{c.label}
                </button>
              )
            })}
          </div>
          {contributionTypes.includes('other') && (
            <input value={contributionOther} onChange={(e) => setContributionOther(e.target.value.slice(0, 120))} placeholder="Tell us how you'd like to contribute" style={{ ...input, marginTop: 10 }} />
          )}
        </Field>

        <Field label="About your service" hint={`${blurb.length}/300`}>
          <textarea value={blurb} onChange={(e) => setBlurb(e.target.value.slice(0, 300))} rows={4} placeholder="One or two lines on how you help and your experience." style={{ ...input, resize: 'vertical' }} />
        </Field>

        <Field label="Masjids you'll serve" hint="Optional — pick the masjid(s) you'd help. For each, choose whether its committee can contact you directly. The first also acts as your vouching masjid.">
          <MasjidMultiSelect
            selected={selectedMasjids}
            onAdd={(m) => setSelectedMasjids((s) => (s.some((x) => x.id === m.id) ? s : [...s, { id: m.id, name: m.name, city: m.city, directContactOk: false }]))}
            onRemove={(id) => setSelectedMasjids((s) => s.filter((x) => x.id !== id))}
            onToggle={(id, v) => setSelectedMasjids((s) => s.map((x) => (x.id === id ? { ...x, directContactOk: v } : x)))}
          />
        </Field>

        <Field label="Who can see your offer" hint="Choose how widely masjid committees can discover you. Contact stays private — set separately below.">
          <select value={scopeLevel} onChange={(e) => setScopeLevel(e.target.value)} style={input}>
            {SCOPE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          {needsAnchor && (
            <p style={{ margin: '8px 0 0', fontSize: 12.5, color: '#b45309', lineHeight: 1.5 }}>
              {scopeLevel === 'city' || scopeLevel === 'district'
                ? 'Select your vouching masjid above so we know your city/district for this reach.'
                : 'Select your vouching masjid above to appear in nearby rosters.'}
            </p>
          )}
        </Field>

        <Field label="WhatsApp number" hint="Optional. Revealed only after you approve a request — never shown publicly. If you leave this blank, your account phone number is shared instead when you approve.">
          <input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} type="tel" placeholder="e.g. +91 98765 43210" style={input} />
        </Field>

        <Field label="Who can find you">
          <div style={{ display: 'grid', gap: 8 }}>
            {VISIBILITIES.map((v) => (
              <label key={v.value} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', border: `1.5px solid ${visibility === v.value ? GREEN : LINE}`, borderRadius: 10, padding: '11px 13px', cursor: 'pointer', background: visibility === v.value ? '#f5faf8' : '#fff' }}>
                <input type="radio" name="visibility" checked={visibility === v.value} onChange={() => setVisibility(v.value)} style={{ marginTop: 3 }} />
                <span><strong style={{ color: NAVY, fontSize: 14 }}>{v.label}</strong><span style={{ display: 'block', color: SLATE, fontSize: 12.5, marginTop: 2, lineHeight: 1.5 }}>{v.help}</span></span>
              </label>
            ))}
          </div>
        </Field>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 6 }}>
          <button type="button" onClick={() => void save()} disabled={!canSave} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: GREEN, color: '#fff', border: 'none', borderRadius: 10, padding: '12px 22px', fontWeight: 700, fontSize: 14, cursor: canSave ? 'pointer' : 'not-allowed', opacity: canSave ? 1 : 0.55 }}>
            {saving ? <Loader2 className="spin" size={16} /> : <Check size={16} />} {saving ? 'Saving…' : 'Save profile'}
          </button>
          {msg && <span style={{ fontSize: 13.5, fontWeight: 600, color: msg.tone === 'ok' ? GREEN : '#b91c1c' }}>{msg.text}</span>}
        </div>
      </div>
    </Shell>
  )
}

/* Masjid MULTI-select — reuses the app's standard /api/masjids?q= search (same source the
   claim/search/scan flows use). Adds masjids to the serve list; each selected masjid renders a row
   with its OWN direct-contact consent toggle (default OFF = opt-in) and explicit, committee-named
   copy so it's unmistakable which committee gets what. Already-selected masjids are excluded from
   results. No shared picker component exists, so this mirrors the inline search pattern. */
function MasjidMultiSelect({
  selected, onAdd, onRemove, onToggle,
}: {
  selected: SelectedMasjid[]
  onAdd: (m: Masjid) => void
  onRemove: (id: string) => void
  onToggle: (id: string, v: boolean) => void
}) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState<Masjid[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return }
    let alive = true
    setLoading(true)
    const id = setTimeout(() => {
      fetch(`/api/masjids?q=${encodeURIComponent(q.trim())}&limit=6`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (!alive) return; setResults((d?.masjids || []).map((m: any) => ({ id: String(m.id), name: String(m.nameEnglish || m.name || 'Masjid'), city: String(m.city || '') }))) })
        .catch(() => { if (alive) setResults([]) })
        .finally(() => { if (alive) setLoading(false) })
    }, 300)
    return () => { alive = false; clearTimeout(id) }
  }, [q])

  const selectedIds = new Set(selected.map((s) => s.id))
  const visibleResults = results.filter((m) => !selectedIds.has(m.id))

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {/* Selected masjids — each with its own committee-named direct-contact toggle. */}
      {selected.length > 0 && (
        <div style={{ display: 'grid', gap: 8 }}>
          {selected.map((m) => (
            <div key={m.id} style={{ border: `1.5px solid ${LINE}`, borderRadius: 10, padding: '11px 12px', display: 'grid', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <MapPin size={15} style={{ color: GREEN, flex: 'none' }} />
                <span style={{ flex: 1, fontSize: 14, color: NAVY, fontWeight: 600 }}>{m.name}{m.city ? <span style={{ color: SLATE, fontWeight: 400, fontSize: 12.5 }}> · {m.city}</span> : null}</span>
                <button type="button" onClick={() => onRemove(m.id)} aria-label={`Remove ${m.name}`} style={{ background: 'none', border: 'none', cursor: 'pointer', color: SLATE, display: 'inline-flex' }}><X size={15} /></button>
              </div>
              <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', cursor: 'pointer' }}>
                <input type="checkbox" checked={m.directContactOk} onChange={(e) => onToggle(m.id, e.target.checked)} style={{ marginTop: 2, flex: 'none' }} />
                <span style={{ fontSize: 12.5, lineHeight: 1.5, color: m.directContactOk ? NAVY : SLATE }}>
                  {m.directContactOk
                    ? <><strong style={{ color: NAVY }}>{m.name}</strong>&rsquo;s committee can contact you directly on your phone.</>
                    : <>Listed for <strong style={{ color: NAVY }}>{m.name}</strong>&rsquo;s committee — they can request your contact, and you approve before your number is shared.</>}
                </span>
              </label>
            </div>
          ))}
        </div>
      )}

      {/* Search + add */}
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: `1.5px solid ${LINE}`, borderRadius: 10, padding: '0 12px' }}>
          <Search size={15} style={{ color: SLATE }} />
          <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true) }} placeholder="Search a masjid by name or city…" style={{ ...input, border: 'none', padding: '10px 0', flex: 1 }} />
          {loading && <Loader2 className="spin" size={14} style={{ color: SLATE }} />}
        </div>
        {open && visibleResults.length > 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.08)', zIndex: 20, overflow: 'hidden' }}>
            {visibleResults.map((m) => (
              <button key={m.id} type="button" onClick={() => { onAdd(m); setOpen(false); setQ('') }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 12px', background: 'none', border: 'none', borderBottom: `1px solid ${LINE}`, cursor: 'pointer' }}>
                <Plus size={14} style={{ color: GREEN, flex: 'none' }} />
                <span><strong style={{ fontSize: 13.5, color: NAVY }}>{m.name}</strong>{m.city ? <span style={{ color: SLATE, fontSize: 12 }}> · {m.city}</span> : null}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main style={{ maxWidth: 640, margin: '0 auto', padding: '28px 16px 64px' }}>
      <Link href="/account?tab=connect" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: GREEN, fontSize: 13.5, fontWeight: 600, textDecoration: 'none', marginBottom: 14 }}><ArrowLeft size={15} /> Back to account</Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <span style={{ width: 40, height: 40, borderRadius: 12, background: '#eef6f3', display: 'grid', placeItems: 'center', color: GREEN }}><Users size={20} /></span>
        <h1 style={{ fontSize: 23, fontWeight: 800, color: NAVY, margin: 0 }}>How I can serve</h1>
      </div>
      <p style={{ color: SLATE, fontSize: 14, lineHeight: 1.6, margin: '0 0 24px' }}>List your profession and skills so a neighbour can find you. Your contact details stay private until you approve a request.</p>
      {children}
      <style jsx global>{`.spin{animation:connspin 1s linear infinite}@keyframes connspin{to{transform:rotate(360deg)}}`}</style>
    </main>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'grid', gap: 6 }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{label}</span>
      {hint && <span style={{ fontSize: 12, color: SLATE, marginTop: -2, lineHeight: 1.5 }}>{hint}</span>}
      {children}
    </label>
  )
}

function Notice({ title, body, cta }: { title: string; body: string; cta: { href: string; label: string } }) {
  return (
    <div style={{ textAlign: 'center', border: `1px dashed ${LINE}`, borderRadius: 16, padding: 40 }}>
      <Users size={26} style={{ color: '#bbb', marginBottom: 10 }} />
      <div style={{ fontSize: 16, fontWeight: 700, color: NAVY, marginBottom: 6 }}>{title}</div>
      <p style={{ fontSize: 14, color: SLATE, marginBottom: 16, maxWidth: 380, marginInline: 'auto' }}>{body}</p>
      <Link href={cta.href} style={{ display: 'inline-block', background: GREEN, color: '#fff', borderRadius: 10, padding: '10px 18px', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>{cta.label}</Link>
    </div>
  )
}

const input: React.CSSProperties = { border: `1.5px solid ${LINE}`, borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', color: '#222', outline: 'none', background: '#fff', width: '100%' }
const chipBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, background: '#f5faf8', color: GREEN, border: `1.5px solid #cfe3dc`, borderRadius: 10, padding: '0 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }
